var selectContentFunction = () => new Promise((resolve, reject) => reject('Not implemented'));
var openContentInNewTabFunction = () => new Promise((resolve, reject) => reject('Not implemented'));
var openContentInDialogFunction = () => new Promise((resolve, reject) => reject('Not implemented'));
var openContentInCurrentTabFunction = () => new Promise((resolve, reject) => reject('Not implemented'));
var getLatestFunction = () => new Promise((resolve, reject) => reject('Not implemented'));
var uploadFileServiceFunction = () => new Promise((resolve, reject) => reject('Not implemented'));
var getDependentsFunction = () => new Promise((resolve, reject) => reject('Not implemented'));
var getContentFromRefFunction = () => new Promise((resolve, reject) => reject('Not implemented'));
const notImplemented = () => Promise.reject('Not implemented');
var getContentByCustomIdFunction = notImplemented;
var findContentByCustomIdFunction = notImplemented;
var searchContentFunction = notImplemented;

/**
 * A PLANT content revision (header + body)
 * @typedef {Object} PContent
 */

/**
 * Paginated list of contents
 * @typedef {Object} PContentList
 * @property {Array<PContent>} docs - Contents found
 */

/**
 * @typedef {Object} GetContentByCustomIdOptions
 * @property {string} custom_id - Custom ID of the content
 * @property {string} [library_id] - Library where the content is searched. If omitted, the current library is used
 * @property {boolean} [isLink] - Indicates the content is the target of a diagram link (used by the SCORM driver)
 */

/**
 * @typedef {Object} FindContentByCustomIdOptions
 * @property {string} custom_id - Custom ID of the content (e.g. a datasheet tag)
 * @property {string} [type] - Expected content type (e.g. 'datasheet'). Used by the SCORM driver to resolve the file; defaults to 'datasheet'
 * @property {string} [library_id] - Library where the content is searched. If omitted, the current library is used
 */

/**
 * @typedef {Object} SearchContentOptions
 * @property {string} type - Content type to search (e.g. 'datasheetConfig', 'picture', 'vr', 'video', 'document')
 * @property {string} [text] - Text to filter the search. Usually the custom_id of a datasheet, to find related content
 * @property {boolean|number} [hotspot] - If truthy, the `text` search also matches contents whose hotspots reference `text`
 * @property {string} [library_id] - Library where the content is searched. If omitted, the current library is used
 */

/**
 * Registers a driver that implements the PServiceContent
 * @param {Object} PContentDriver
 * @param {function} PContentDriver.selectContentDriver - A driver function to prompt the user to select content
 * @param {function(GetContentByCustomIdOptions): Promise<PContent>} [PContentDriver.getContentByCustomIdDriver] - A driver function to get a single content by custom_id
 * @param {function(FindContentByCustomIdOptions): Promise<Array<PContent>>} [PContentDriver.findContentByCustomIdDriver] - A driver function to find a content by custom_id, returned in an array
 * @param {function(SearchContentOptions): Promise<PContentList>} [PContentDriver.searchContentDriver] - A driver function to search contents by type/text/hotspot
 * @param {function} PContentDriver.openContentInNewTabDriver - A driver function to open content in a new tab
 * @param {function} PContentDriver.openContentInDialogDriver - A driver function to open content in a dialog
 * @param {function} PContentDriver.openContentInCurrentTabDriver - A driver function to open content in current tab
 * @param {function} PContentDriver.uploadFileServiceDriver - A driver function to upload a file in current Draft
 * @param {function} PContentDriver.getDependentsFunction - A driver function to get dependents of current Content
 * @param {function} PContentDriver.getContentFromRefFunction - A driver function to get the content from a reference(absolute or relative)
 */
const registerDriver = function ({
	selectContentDriver,
	getContentByCustomIdDriver,
	findContentByCustomIdDriver,
	searchContentDriver,
	getLatestDriver,
	openContentInNewTabDriver,
	openContentInDialogDriver,
	openContentInCurrentTabDriver,
	uploadFileServiceDriver,
	getDependentsDriver,
	getContentFromRefDriver
}) {
	selectContentFunction = selectContentDriver;
	getContentByCustomIdFunction = getContentByCustomIdDriver || notImplemented;
	findContentByCustomIdFunction = findContentByCustomIdDriver || notImplemented;
	searchContentFunction = searchContentDriver || notImplemented;
	openContentInNewTabFunction = openContentInNewTabDriver;
	openContentInDialogFunction = openContentInDialogDriver;
	openContentInCurrentTabFunction = openContentInCurrentTabDriver;
	getLatestFunction = getLatestDriver;
	uploadFileServiceFunction = uploadFileServiceDriver;
	getDependentsFunction = getDependentsDriver;
	getContentFromRefFunction = getContentFromRefDriver;
};
/**
 * Prompts user to select content
 * @param {Object} selectContentOptions
 * @param {string | array<string>} selectContentOptions.type - Content type to find
 * @returns {Promise<PContent>} The PContent selected by the user
 */
const selectContent = function (selectContentOptions) {
	return selectContentFunction(selectContentOptions);
};

/**
 * Get a single content by its custom_id (e.g. to open it with `openContent`)
 * @param {GetContentByCustomIdOptions} options
 * @returns {Promise<PContent>} The content found. Rejects if no content is found
 */
const getContentByCustomId = function (options) {
	return getContentByCustomIdFunction(options);
};

/**
 * Find a content by its custom_id (e.g. the datasheet of a tag)
 * @param {FindContentByCustomIdOptions} options
 * @returns {Promise<Array<PContent>>} An array with the content found. Rejects if no content is found
 */
const findContentByCustomId = function (options) {
	return findContentByCustomIdFunction(options);
};

/**
 * Search contents by type and, optionally, by text/hotspot (e.g. content related to a datasheet)
 * @param {SearchContentOptions} options
 * @returns {Promise<PContentList>} A content list whose `docs` property holds the matching contents
 */
const searchContent = function (options) {
	return searchContentFunction(options);
};

/**
 * Open content in a new tab
 * @param {string | Object} urlOrRevision - URL or revision object to open.
 * @param {string} urlParams - Additional query string parameters to append to the generated URL.
 */
const openContentInNewTab = function (urlOrRevision, urlParams) {
	return openContentInNewTabFunction(urlOrRevision, urlParams);
};

/**
 * Open content in a dialog
 * @param {string | Object} urlOrRevision - URL or revision object to open.
 */
const openContentInDialog = function (urlOrRevision) {
	return openContentInDialogFunction(urlOrRevision);
};

/**
 * Open content in current tab
 * @param {Object} revisionLocator - Descriptor used to locate the content revision to open.
 */
const openContentInCurrentTab = function (revisionLocator) {
	return openContentInCurrentTabFunction(revisionLocator);
};

/**
 * Get latest versions
 * @param {Array} getLatestOptions
 * @returns {Promise<Array>} The promise as an array of latest versions
 */
const getLatest = function (getLatestOptions) {
	return getLatestFunction(getLatestOptions);
};
/**
 * Upload File Service
 * @param {File} file
 * @returns {Promise<{_file: String, url: String}>} The promise as an array of latest versions
 */
const uploadFileService = function (file, contentId) {
	return uploadFileServiceFunction(file, contentId);
};

/**
 * Get Dependents
 * @param {Object} content
 * @returns {Promise<Array>} The promise as an array of content dependents
 */
const getDependents = function (content) {
	return getDependentsFunction(content);
};

const getContentFromRef = function (ref, dependencies) {
	return getContentFromRefFunction(ref, dependencies);
};

export default {
	selectContent,
	getContentByCustomId,
	findContentByCustomId,
	searchContent,
	registerDriver,
	getLatest,
	openContentInNewTab,
	openContentInDialog,
	openContentInCurrentTab,
	uploadFileService,
	getDependents,
	getContentFromRef
};
